import type { TelemetryQueryRequest } from '../../Services/serviceURL';
import type { SensorData } from './analysisTabSensorTypes';
import { formatLocalDateTime, parseLocalDateTime } from './analysisTabDateTime';

export function isValidAssetGuid(guid: string): boolean {
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return guidRegex.test(guid);
}

export function filterUniqueKnownChannels(channelIds: string[], sensors: SensorData[]): string[] {
  const known = new Set(sensors.map(sensor => sensor.channelId));
  const unique: string[] = [];
  for (const channelId of channelIds) {
    if (!known.has(channelId) || unique.includes(channelId)) {
      continue;
    }
    unique.push(channelId);
  }
  return unique;
}

export function buildTelemetryQueryRequest(
  assetId: string,
  channelIds: string[],
  selectedTimeRange: string,
  timeRangeIndex: number,
  getDateRange: () => { startTime: string; endTime: string }
): TelemetryQueryRequest {
  const request: TelemetryQueryRequest = {
    AssetId: assetId,
    ChannelIds: channelIds,
    timeRange: timeRangeIndex,
  };

  if (selectedTimeRange === 'Custom date range') {
    const { startTime, endTime } = getDateRange();
    request.StartTime = startTime;
    request.EndTime = endTime;
  } else {
    request.timeRange = timeRangeIndex;
  }

  return request;
}

type TelemetryChannel = {
  channelId: string;
  data?: { timestamp: string; value: number | null }[];
  uom?: string;
  minValue?: number;
  maxValue?: number;
  alarmHData?: { timestamp: string; value: number }[];
  alarmHHData?: { timestamp: string; value: number }[];
  alarmLData?: { timestamp: string; value: number }[];
  alarmLLData?: { timestamp: string; value: number }[];
};

function mapApiPoints(channelData: TelemetryChannel): { date: string; value: number }[] {
  if (!channelData.data) {
    return [];
  }

  const allPoints: { date: string; value: number }[] = [];
  for (const point of channelData.data) {
    if (point.value === null || point.value === undefined) {
      continue;
    }
    const parsedTimeMs = parseLocalDateTime(point.timestamp);
    if (isNaN(parsedTimeMs)) {
      continue;
    }
    allPoints.push({
      date: formatLocalDateTime(new Date(parsedTimeMs)),
      value: point.value,
    });
  }
  return allPoints;
}

function filterPointsForRange(
  allPoints: { date: string; value: number }[],
  startMs: number,
  endMs: number
): { date: string; value: number }[] {
  let sensorDataPoints = allPoints.filter(point => {
    const ts = parseLocalDateTime(point.date);
    return ts >= startMs && ts <= endMs;
  });

  if (sensorDataPoints.length === 0 && allPoints.length > 0) {
    sensorDataPoints = allPoints;
  }

  if (sensorDataPoints.length === 0 && allPoints.length > 0) {
    const timestamps = allPoints.map(point => parseLocalDateTime(point.date));
    const dataEnd = Math.max(...timestamps);
    const timeDiffHours = Math.abs(startMs - dataEnd) / (1000 * 60 * 60);
    if (timeDiffHours <= 12) {
      sensorDataPoints = allPoints;
    }
  }

  return sensorDataPoints;
}

function mergeChannelIntoSensor(
  sensor: SensorData,
  channelData: TelemetryChannel,
  startMs: number,
  endMs: number
): SensorData {
  const allPoints = mapApiPoints(channelData);
  const sensorDataPoints = filterPointsForRange(allPoints, startMs, endMs);

  return {
    ...sensor,
    dataPoints: sensorDataPoints,
    unit: channelData.uom || sensor.unit,
    minValue: channelData.minValue,
    maxValue: channelData.maxValue,
    alarmHData: channelData.alarmHData || [],
    alarmHHData: channelData.alarmHHData || [],
    alarmLData: channelData.alarmLData || [],
    alarmLLData: channelData.alarmLLData || [],
  };
}

export function applyTelemetryToSensors(
  sensors: SensorData[],
  channelIds: string[],
  telemetryChannels: TelemetryChannel[],
  startMs: number,
  endMs: number
): SensorData[] {
  const channelMap = new Map<string, TelemetryChannel>();
  for (const channel of telemetryChannels) {
    channelMap.set(channel.channelId, channel);
  }

  return sensors.map(sensor => {
    if (!channelIds.includes(sensor.channelId)) {
      return sensor;
    }

    const channelData = channelMap.get(sensor.channelId);
    if (!channelData) {
      return { ...sensor, dataPoints: [] };
    }

    return mergeChannelIntoSensor(sensor, channelData, startMs, endMs);
  });
}

export function sensorsHaveAnyData(sensors: SensorData[], channelIds: string[]): boolean {
  for (const sensor of sensors) {
    if (!channelIds.includes(sensor.channelId)) {
      continue;
    }
    if (sensor.dataPoints.length > 0) {
      return true;
    }
  }
  return false;
}

export function getSensorDataTimestamps(sensors: SensorData[]): number[] {
  const timestamps: number[] = [];
  for (const sensor of sensors) {
    for (const point of sensor.dataPoints) {
      timestamps.push(new Date(point.date).getTime());
    }
  }
  return timestamps;
}
